import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { jwtSelector } from "src/common/state/selectors/authSelector";
import { IsLoading } from "src/common/components/AppState/IsLoading";
import { ButtonIcon } from "src/common/components/Button";
import PlaceholderWithIcon from "src/common/components/PlaceholderWithIcon/PlaceholderWithIcon";
import CheckAll from "src/common/components/UsersTable/CheckAll";
import type { IAllowedItem } from "src/common/components/UsersTable/DisplayAllowedItems";
import { buildEnduserRow } from "src/common/components/UsersTable/buildEnduserRow";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import {
  getSearchIndex,
  fetchSearchIndex,
  getSelectedProjectGroup,
} from "src/features/projectGroups/state/projectGroupsSlice";
import {
  getEndusers,
  getTeamAsAdmin,
  setDeleteUsersDraft,
} from "src/features/team/state/teamSlice";
import { SearchAutocomplete } from "src/common/components/SearchAutocomplete/SearchAutocomplete";
import { DeleteElementsSelectedFromList } from "src/common/components/DeleteElementsSelectedFromList/DeleteElementsSelectedFromList";
import {
  getConfirmationModalStateShow,
  openConfirmationModal,
} from "src/common/state/slice/modal/modalSlice";
import TableComp from "src/common/components/Table/Table";

export const PROJECT_GROUP_LEARNERS_LIST_COMPONENT = "projectGroupLearnersListComponent";
export const PROJECT_GROUP_LEARNERS_TABLE_COMPONENT = "projectGroupLearnersTableComponent";

const SIMPLIFIED_USER_EMAIL_PATTERN = "@sneakysneaky.ua";

const TABLE_HEIGHT_RATIO = 2 / 3;

/**
 * Story 19.2: Project-Group Learners List
 * Displays learners (endusers) who have access to modules within the current project-group
 */
const ProjectGroupLearnersList = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: projectGroupId } = useParams();

  const searchIndex = useAppSelector(getSearchIndex);
  const endusers = useAppSelector(getEndusers);
  const confirmationModalStateShow = useAppSelector(getConfirmationModalStateShow);
  const jwt = useAppSelector(jwtSelector);
  const selectedProjectGroup = useAppSelector(getSelectedProjectGroup);

  const [displayedEndusers, setDisplayedEndusers] = useState(endusers);
  const [isCheck, setIsCheck] = useState<Array<string>>([]);
  const [filterByText, setFilterByText] = useState("");
  const [tableHeight, setTableHeight] = useState(0);

  const maxRowsLengthRef = useRef(0);

  const projectsInThisProjectGroup = useMemo(() => {
    return searchIndex.filter(
      (item) => item.type === "project" && item.projectGroupId === Number(projectGroupId),
    );
  }, [searchIndex, projectGroupId]);

  const projectIds = useMemo(() => {
    return new Set(projectsInThisProjectGroup.map((p) => String(p.id)));
  }, [projectsInThisProjectGroup]);

  const endusersByProjectGroup = useMemo(() => {
    if (!searchIndex.length || !endusers.length) return [];

    return endusers.filter((user) => {
      return user.permissions?.some((perm) => {
        const projectId = String(perm?.project ? perm.project.id : "");
        return projectId && projectIds.has(projectId);
      });
    });
  }, [endusers, projectIds, searchIndex.length]);

  useEffect(() => {
    setDisplayedEndusers(endusersByProjectGroup);
    setIsCheck([]);
    maxRowsLengthRef.current = Math.max(maxRowsLengthRef.current, endusersByProjectGroup.length);
  }, [endusersByProjectGroup]);

  const handleOnSearch = (string: string) => {
    setIsCheck([]);
    if (string === "") {
      setFilterByText("");
    }
  };

  const handleOnSelect = (item: { name: string }) => {
    setFilterByText(item.name);
  };

  useEffect(() => {
    let filteredUsers = endusersByProjectGroup;

    if (filterByText) {
      filteredUsers = filteredUsers.filter((user) =>
        user.email.toLowerCase().includes(filterByText.toLowerCase()),
      );
    }

    setDisplayedEndusers(filteredUsers);
  }, [filterByText, endusersByProjectGroup]);

  useEffect(() => {
    const calculateHeight = () => {
      const divinaProportione = Math.round(window.innerHeight * TABLE_HEIGHT_RATIO);
      setTableHeight(divinaProportione);
    };

    calculateHeight();

    window.addEventListener("resize", calculateHeight);
    return () => {
      window.removeEventListener("resize", calculateHeight);
    };
  }, []);

  useEffect(() => {
    if (!confirmationModalStateShow) {
      setIsCheck([]);
    }
  }, [confirmationModalStateShow]);

  const handleSelectAll = () => {
    if (isCheck.length > 0) {
      setIsCheck([]);
    } else {
      setIsCheck(displayedEndusers.map((li) => String(li.id)));
    }
  };

  const handleClick = (e: React.MouseEvent<HTMLInputElement>) => {
    const { id, checked } = e.currentTarget;

    if (checked) {
      const nextValues = [...isCheck, id];
      setIsCheck(nextValues);
    } else {
      setIsCheck(isCheck.filter((item) => item !== id));
    }
  };

  useEffect(() => {
    if (!jwt) {
      return;
    }

    void dispatch(fetchSearchIndex({}));
    void dispatch(getTeamAsAdmin({ componentId: PROJECT_GROUP_LEARNERS_LIST_COMPONENT }));
  }, [dispatch, jwt, location.key]);

  if (selectedProjectGroup?.auth_type === "none") {
    return (
      <div className="w-full h-1/2 flex justify-center mt-16">
        <PlaceholderWithIcon
          title={t("pages.projectGroup.learners.anonymousDisclaimerTitle")}
          text={t("pages.projectGroup.learners.anonymousDisclaimerText")}
          heroIcon="InformationCircleIcon"
          className="flex flex-col items-center h-[6pc]"
        />
      </div>
    );
  }

  const colHeaders = [
    "",
    t("general.firstname"),
    t("general.lastname"),
    t("general.identifier"),
    t("pages.projectGroup.learners.modules"),
    t("general.actions"),
  ];

  const rows = displayedEndusers.map((enduser, i: number) => {
    const moduleCount =
      enduser.permissions?.filter((perm) => {
        const projectId = String(perm?.project ? perm.project.id : "");
        return projectId && projectIds.has(projectId);
      }).length || 0;

    const totalProjectsInGroup = projectsInThisProjectGroup.length;

    const hasAccessToEverything = moduleCount === totalProjectsInGroup;

    const enduserIsSimplified = enduser.email.includes(SIMPLIFIED_USER_EMAIL_PATTERN);

    const allowedItems: IAllowedItem[] = [];
    enduser.permissions?.forEach((p) => {
      const projectId = String(p?.project ? p.project.id : "");
      if (projectId && projectIds.has(projectId) && p.project?.name) {
        allowedItems.push({
          name: p.project.name,
          published: p.project.status === "Published",
        });
      }
    });

    return buildEnduserRow({
      enduser,
      index: i,
      isChecked: isCheck.includes(String(enduser.id)),
      enduserIsSimplified,
      hasAccessToEverything,
      allowedItems,
      t,
      dispatch,
      onCheckboxClick: handleClick,
      onEditNavigate: () => navigate(`/courses/${projectGroupId}/learners/${enduser.id}`),
    });
  });

  return (
    <>
      <div className="flex flex-col items-center w-full h-full">
        <div className="flex mt-4 w-[96%] justify-between items-end">
          <SearchAutocomplete
            items={displayedEndusers.map((user) => {
              return { name: user.email };
            })}
            handleOnSearch={handleOnSearch}
            handleOnSelect={handleOnSelect}
          />
          <ButtonIcon
            icon="PlusIcon"
            styled="btn-primary-fill h-12"
            text={t("pages.projectGroup.learners.addNewLearners")}
            onClick={() => navigate(`/courses/${projectGroupId}/learners/new`)}
          />
        </div>
        <DeleteElementsSelectedFromList
          className="self-start ml-12 mt-2"
          isCheck={isCheck}
          selectedTextSingular={t("pages.participants.selectedUserSingular")}
          selectedTextPlurial={t("pages.participants.selectedUserPlurial")}
          deleteButtonTextSingular={t("pages.participants.deleteSelectedUserSingular")}
          deleteButtonTextPlurial={t("pages.participants.deleteSelectedUserPlural")}
          deleteCallback={() => {
            dispatch(
              setDeleteUsersDraft({
                type: isCheck.length > 1 ? "delete_endusers" : "delete_enduser",
                usersToBeDeleted: endusers.filter((c) => isCheck.includes(String(c.id))),
              }),
            );
            dispatch(openConfirmationModal("confirm-delete-users"));
          }}
        />
        <IsLoading
          componentId={PROJECT_GROUP_LEARNERS_LIST_COMPONENT}
          showSuccess={false}
          spinnerPlaceholder
        >
          <IsLoading
            componentId={PROJECT_GROUP_LEARNERS_TABLE_COMPONENT}
            showSuccess={false}
            spinnerPlaceholder
          >
            <TableComp
              key={tableHeight}
              style={{ maxHeight: `${tableHeight}px` }}
              className="w-[96%] mt-2 mb-2 personalize-scroll-visible"
              colHeaders={colHeaders}
              nonSortableColumns={[t("general.actions")]}
              rows={rows}
              disableCriteria={() => false}
              checkAll={
                <CheckAll
                  className={displayedEndusers.length === 0 ? "hidden" : ""}
                  checked={isCheck.length > 0}
                  onChange={handleSelectAll}
                />
              }
              listTofilter={displayedEndusers}
              selectFromListInTable
              setListOfItemsFilter={setDisplayedEndusers}
              isUserList={true}
              emptyPlaceholder={
                <div className="flex m-8 items-center justify-center">
                  {maxRowsLengthRef.current
                    ? t("pages.projectGroup.learners.noLearnersMatchFilters")
                    : t("pages.projectGroup.learners.noLearnersCTA")}
                </div>
              }
            />
          </IsLoading>
        </IsLoading>
      </div>
    </>
  );
};

export default ProjectGroupLearnersList;
